import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import COLORS from '../../constants/colors';
import { locations } from '../../routes/locations';
import { User, UserStatus } from '../../types/user';
import { formatDate } from '../../utils/date';

/** Mirrors the status colours already used in the user list, so the two screens agree. */
const statusColor: Record<UserStatus, 'default' | 'error' | 'info' | 'success' | 'warning'> = {
  [UserStatus.REGISTERED]: 'info',
  [UserStatus.ACTIVE]: 'success',
  [UserStatus.PENDING]: 'info',
  [UserStatus.INACTIVE]: 'warning',
  [UserStatus.MANUAL_CHECK_REQUIRED]: 'warning',
  [UserStatus.WAITING_ALPACA_ACTIVATION]: 'warning',
  [UserStatus.BLOCKED]: 'error',
};

const initialsOf = (fullName?: string) =>
  (fullName || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

interface MetaItemProps {
  label: string;
  value?: React.ReactNode;
}

function MetaItem({ label, value }: MetaItemProps) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: COLORS.LIGHT_GRAY,
          mb: '2px',
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: COLORS.MAIN_DARK,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

interface UserPageHeaderProps {
  user: User | null;
  isLoading: boolean;
}

export function UserPageHeader({ user, isLoading }: UserPageHeaderProps) {
  const navigate = useNavigate();
  const status = user?.status?.name;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: '16px', sm: '20px 24px' },
        mb: '16px',
        borderRadius: '12px',
        border: `1px solid ${alpha(COLORS.MAIN_DARK, 0.08)}`,
      }}
    >
      <Button
        onClick={() => navigate(locations.userList())}
        startIcon={<ArrowBackIcon sx={{ fontSize: '18px' }} />}
        size="small"
        sx={{ mb: '12px', ml: '-6px', textTransform: 'none', color: COLORS.DARK_GREY, fontWeight: 500 }}
      >
        Back to users
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.12),
            color: '#1E72A8',
            fontWeight: 600,
            fontSize: '1.125rem',
          }}
        >
          {initialsOf(user?.fullName)}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
          {isLoading && !user ? (
            <Skeleton variant="text" width={220} height={32} />
          ) : (
            <Typography
              component="h1"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: '28px',
                color: COLORS.MAIN_DARK,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.fullName || 'User'}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY }}>
            {user?.id ? `ID ${user.id}` : ''}
          </Typography>
        </Box>

        {status && (
          <Chip
            label={status}
            size="small"
            color={statusColor[status] || 'default'}
            variant="outlined"
            sx={{ fontWeight: 600, borderRadius: '6px' }}
          />
        )}
      </Box>

      <Divider sx={{ my: '16px', borderColor: alpha(COLORS.MAIN_DARK, 0.08) }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
          gap: '16px',
        }}
      >
        <MetaItem label="Email" value={user?.email} />
        <MetaItem label="Mobile" value={user?.mobile} />
        <MetaItem label="Group" value={user?.group?.nameEn} />
        <MetaItem label="Registered" value={user?.createdAt ? formatDate(String(user.createdAt)) : undefined} />
      </Box>
    </Paper>
  );
}
